/**
 * Normalizes model JSON for `visa_metadata.process_steps` to a canonical envelope.
 * Keep in sync with `supabase/functions/_shared/visa-enrichment-normalize.ts`.
 */

export type VisaEnrichmentLink = {
  label: string
  url?: string
  notFound?: boolean
}

export type VisaSourceKind =
  | 'embassy_in_applicant_country'
  | 'destination_immigration'
  | 'application_portal'
  | 'passport_delivery'
  | 'visa_center_city'
  | 'other'

const VALID_SOURCE_KINDS: readonly VisaSourceKind[] = [
  'embassy_in_applicant_country',
  'destination_immigration',
  'application_portal',
  'passport_delivery',
  'visa_center_city',
  'other',
]

function coerceVisaSourceKind(rawKind: unknown): VisaSourceKind {
  const raw = typeof rawKind === "string" ? rawKind.toLowerCase().trim() : ""
  return (VALID_SOURCE_KINDS as readonly string[]).includes(raw)
    ? (raw as VisaSourceKind)
    : "other"
}

export type VisaOfficialSource = {
  kind: VisaSourceKind
  title: string
  notFound: boolean
  url?: string
  city?: string
  notes?: string
}

export type VisaCityRule = {
  city: string
  jurisdiction?: string
  guidance: string
  links?: VisaEnrichmentLink[]
}

export type VisaDeliveryRow = {
  title: string
  description: string
  notFound?: boolean
  url?: string
}

export type VisaEnrichmentStep = {
  order: number
  title: string
  title_pt?: string
  description: string
  description_pt?: string
  where: string
  links: VisaEnrichmentLink[]
  documentsNeeded: string[]
  notes: string
  commonMistakes: string[]
  dependencies: number[]
  appliesTo: string[]
  cityRules: VisaCityRule[]
  restrictions: string[]
  estimatedDays: string
  costUsd: number
}

export type VisaEnrichmentEnvelope = {
  steps: VisaEnrichmentStep[]
  sources: VisaOfficialSource[]
  routingNotes: string[]
  cityRules: VisaCityRule[]
  delivery: VisaDeliveryRow[]
  total_cost_usd: number
  total_estimated_days: string
  common_pitfalls: string[]
  success_tips: string[]
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v)
}

function unwrapEnrichedPayload(obj: Record<string, unknown>): Record<string, unknown> {
  let cur = obj
  for (let depth = 0; depth < 4; depth++) {
    let inner: Record<string, unknown> | null = null
    for (const k of ["data", "result", "output", "response", "payload", "visa_process", "enriched"]) {
      const v = cur[k]
      if (isRecord(v) && ("steps" in v || "step" in v)) {
        inner = v
        break
      }
    }
    if (!inner) break
    cur = inner
  }
  return cur
}

function applyTopLevelAliases(o: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...o }
  const pickFirst = (canonical: string, ...alts: string[]) => {
    if (out[canonical] != null && out[canonical] !== "") return
    for (const a of alts) {
      if (out[a] != null && out[a] !== "") {
        out[canonical] = out[a]
        return
      }
    }
  }
  pickFirst("total_cost_usd", "totalcostusd", "totalCostUsd")
  pickFirst("total_estimated_days", "totalestimateddays", "totalEstimatedDays")
  if (out.common_pitfalls == null && out.commonpitfalls != null) out.common_pitfalls = out.commonpitfalls
  if (out.success_tips == null && out.successtips != null) out.success_tips = out.successtips
  if (out.routingNotes == null && out.routing_notes != null) out.routingNotes = out.routing_notes
  if (out.cityRules == null && out.city_rules != null) out.cityRules = out.city_rules
  return out
}

function mapStepAliases(s: Record<string, unknown>): Record<string, unknown> {
  const o: Record<string, unknown> = { ...s }
  if (o.title == null && o.step_title != null) o.title = o.step_title
  if (
    (o.title == null || (typeof o.title === "string" && !o.title.trim())) &&
    typeof o.title_en === "string" &&
    o.title_en.trim()
  ) {
    o.title = o.title_en
  }
  if (o.description == null && o.step_description != null) o.description = o.step_description
  if (
    (o.description == null || (typeof o.description === "string" && !String(o.description).trim())) &&
    typeof o.description_en === "string" &&
    o.description_en.trim()
  ) {
    o.description = o.description_en
  }
  if (o.documentsNeeded == null && o.documents_needed != null) o.documentsNeeded = o.documents_needed
  if (o.commonMistakes == null && o.common_mistakes != null) o.commonMistakes = o.common_mistakes
  if (o.estimatedDays == null && o.estimated_days != null) o.estimatedDays = o.estimated_days
  if (o.costUsd == null && o.cost_usd != null) o.costUsd = o.cost_usd
  if (o.appliesTo == null && o.applies_to != null) o.appliesTo = o.applies_to
  if (o.cityRules == null && o.city_rules != null) o.cityRules = o.city_rules
  return o
}

function normalizeStringList(v: unknown): string[] {
  if (Array.isArray(v)) {
    return v
      .filter((x): x is string => typeof x === "string")
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
  }
  if (typeof v === "string") {
    const t = v.trim()
    if (!t) return []
    if (t.includes("\n")) return t.split(/\r?\n/).map((s) => s.trim()).filter((s) => s.length > 0)
    return [t]
  }
  return []
}

function coerceOrder(v: unknown, fallback: number): number {
  if (typeof v === "number" && Number.isFinite(v)) return v
  if (typeof v === "string") {
    const n = Number.parseInt(v.trim(), 10)
    if (Number.isFinite(n)) return n
  }
  return fallback
}

function coerceEstimatedDays(v: unknown): string {
  if (typeof v === "string") return v
  if (typeof v === "number" && Number.isFinite(v)) return String(v)
  return ""
}

function coerceCostUsd(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v
  if (typeof v === "string") {
    const n = Number.parseFloat(v.trim())
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

function isPlausibleHttpUrl(s: string): boolean {
  return /^https?:\/\//i.test(s.trim())
}

export function normalizeEnrichmentLink(raw: unknown): VisaEnrichmentLink | null {
  if (!isRecord(raw)) return null
  const label =
    (typeof raw.label === "string" ? raw.label.trim() : "") ||
    (typeof raw.title === "string" ? raw.title.trim() : "")
  if (!label) return null
  const notFoundExplicit = raw.notFound === true
  const urlRaw = typeof raw.url === "string" ? raw.url.trim() : ""
  const hasUrl = urlRaw.length > 0 && isPlausibleHttpUrl(urlRaw)
  const notFound = notFoundExplicit || !hasUrl
  if (notFound) return { label, notFound: true }
  return { label, url: urlRaw }
}

function normalizeLinksArray(v: unknown): VisaEnrichmentLink[] {
  if (!Array.isArray(v)) return []
  const out: VisaEnrichmentLink[] = []
  for (const item of v) {
    const l = normalizeEnrichmentLink(item)
    if (l) out.push(l)
  }
  return out
}

export function normalizeOfficialSource(raw: unknown): VisaOfficialSource | null {
  if (!isRecord(raw)) return null
  const title = typeof raw.title === "string" ? raw.title.trim() : ""
  const kind = coerceVisaSourceKind(raw.kind)
  if (!title) return null
  const notFoundExplicit = raw.notFound === true
  const urlRaw = typeof raw.url === "string" ? raw.url.trim() : ""
  const hasUrl = urlRaw.length > 0 && isPlausibleHttpUrl(urlRaw)
  const notFound = notFoundExplicit || !hasUrl
  const city = typeof raw.city === "string" && raw.city.trim() ? raw.city.trim() : undefined
  const notes = typeof raw.notes === "string" && raw.notes.trim() ? raw.notes.trim() : undefined
  const base: VisaOfficialSource = { kind, title, notFound }
  if (!notFound) base.url = urlRaw
  if (city) base.city = city
  if (notes) base.notes = notes
  return base
}

function normalizeSourcesArray(v: unknown): VisaOfficialSource[] {
  if (!Array.isArray(v)) return []
  const out: VisaOfficialSource[] = []
  for (const item of v) {
    const s = normalizeOfficialSource(item)
    if (s) out.push(s)
  }
  return out
}

function normalizeCityRule(raw: unknown): VisaCityRule | null {
  if (!isRecord(raw)) return null
  const city = typeof raw.city === "string" ? raw.city.trim() : ""
  const guidance = typeof raw.guidance === "string" ? raw.guidance : String(raw.guidance ?? "")
  if (!city || !guidance.trim()) return null
  const jurisdiction =
    typeof raw.jurisdiction === "string" && raw.jurisdiction.trim() ? raw.jurisdiction.trim() : undefined
  const links = normalizeLinksArray(raw.links)
  const o: VisaCityRule = { city, guidance: guidance.trim() }
  if (jurisdiction) o.jurisdiction = jurisdiction
  if (links.length > 0) o.links = links
  return o
}

function normalizeCityRulesArray(v: unknown): VisaCityRule[] {
  if (!Array.isArray(v)) return []
  return v.map(normalizeCityRule).filter((x): x is VisaCityRule => x != null)
}

function normalizeDeliveryRow(raw: unknown): VisaDeliveryRow | null {
  if (!isRecord(raw)) return null
  const title = typeof raw.title === "string" ? raw.title.trim() : ""
  const description = typeof raw.description === "string" ? raw.description : String(raw.description ?? "")
  if (!title || !description.trim()) return null
  const notFoundExplicit = raw.notFound === true
  const urlRaw = typeof raw.url === "string" ? raw.url.trim() : ""
  const hasUrl = urlRaw.length > 0 && isPlausibleHttpUrl(urlRaw)
  const notFound = notFoundExplicit || !hasUrl
  const o: VisaDeliveryRow = { title, description: description.trim() }
  if (notFound) o.notFound = true
  else o.url = urlRaw
  return o
}

function normalizeDeliveryArray(v: unknown): VisaDeliveryRow[] {
  if (!Array.isArray(v)) return []
  return v.map(normalizeDeliveryRow).filter((x): x is VisaDeliveryRow => x != null)
}

function resourcesToSources(v: unknown): VisaOfficialSource[] {
  if (!Array.isArray(v)) return []
  const out: VisaOfficialSource[] = []
  for (const r of v) {
    if (!isRecord(r)) continue
    const title = typeof r.title === "string" ? r.title.trim() : ""
    const urlRaw =
      typeof r.url === "string"
        ? r.url.trim()
        : typeof r.link === "string"
          ? r.link.trim()
          : typeof r.href === "string"
            ? r.href.trim()
            : ""
    const kind = coerceVisaSourceKind(r.type)
    if (!title) continue
    const hasUrl = urlRaw.length > 0 && isPlausibleHttpUrl(urlRaw)
    out.push({
      kind,
      title,
      notFound: !hasUrl,
      ...(hasUrl ? { url: urlRaw } : {}),
    })
  }
  return out
}

function normalizeDependencies(v: unknown): number[] {
  const depsRaw = v
  const dependencies: number[] = []
  if (Array.isArray(depsRaw)) {
    for (const d of depsRaw) {
      if (typeof d === "number" && Number.isFinite(d)) dependencies.push(d)
      else if (typeof d === "string") {
        const n = Number.parseInt(d.trim(), 10)
        if (Number.isFinite(n)) dependencies.push(n)
      }
    }
  }
  return dependencies
}

/**
 * Coerces arbitrary model JSON into {@link VisaEnrichmentEnvelope} or returns null.
 */
export function normalizeVisaEnrichmentPayload(raw: unknown): VisaEnrichmentEnvelope | null {
  if (!isRecord(raw)) return null
  const base = applyTopLevelAliases(unwrapEnrichedPayload(raw))

  let stepsRaw: unknown = base.steps
  if (stepsRaw == null && base.step != null) stepsRaw = base.step

  const stepRecords: Record<string, unknown>[] = []
  if (Array.isArray(stepsRaw)) {
    for (const item of stepsRaw) {
      if (isRecord(item)) stepRecords.push(mapStepAliases(item))
    }
  } else if (isRecord(stepsRaw)) {
    stepRecords.push(mapStepAliases(stepsRaw))
  }

  const steps: VisaEnrichmentStep[] = []
  for (let i = 0; i < stepRecords.length; i++) {
    const sm = stepRecords[i]!
    const order = coerceOrder(sm.order ?? i + 1, i + 1)
    const titleRaw = typeof sm.title === "string" ? sm.title.trim() : ""
    const title = titleRaw.length > 0 ? titleRaw : `Step ${order || i + 1}`
    const description =
      typeof sm.description === "string" ? sm.description : String(sm.description ?? "")
    const title_pt =
      typeof sm.title_pt === "string" && sm.title_pt.trim() ? sm.title_pt.trim() : undefined
    const description_pt =
      typeof sm.description_pt === "string" && sm.description_pt.trim()
        ? sm.description_pt.trim()
        : undefined
    const where = typeof sm.where === "string" ? sm.where : String(sm.where ?? "")
    const notes = typeof sm.notes === "string" ? sm.notes : String(sm.notes ?? "")
    const estimatedDays = coerceEstimatedDays(sm.estimatedDays ?? sm.estimated_days)
    const costUsd = coerceCostUsd(sm.costUsd ?? sm.cost_usd)
    const documentsNeeded = normalizeStringList(
      sm.documentsNeeded ?? sm.documents_needed ?? sm.docs_needed,
    )
    const commonMistakes = normalizeStringList(sm.commonMistakes ?? sm.common_mistakes ?? sm.commonmistakes)
    const appliesTo = normalizeStringList(sm.appliesTo ?? sm.applies_to)
    const restrictions = normalizeStringList(sm.restrictions)
    const links = normalizeLinksArray(sm.links)
    const cityRules = normalizeCityRulesArray(sm.cityRules ?? sm.city_rules)
    const dependencies = normalizeDependencies(sm.dependencies ?? sm.dependency)

    steps.push({
      order,
      title,
      description,
      where,
      links,
      documentsNeeded,
      notes,
      commonMistakes,
      dependencies,
      appliesTo,
      cityRules,
      restrictions,
      estimatedDays,
      costUsd,
      ...(title_pt ? { title_pt } : {}),
      ...(description_pt ? { description_pt } : {}),
    })
  }

  if (steps.length === 0) return null

  let sources = normalizeSourcesArray(base.sources)
  if (sources.length === 0 && base.resources != null) {
    sources = resourcesToSources(base.resources)
  }

  const routingNotes = normalizeStringList(base.routingNotes ?? base.routing_notes)
  const cityRulesTop = normalizeCityRulesArray(base.cityRules ?? base.city_rules)
  const delivery = normalizeDeliveryArray(base.delivery)

  let total_cost_usd = coerceCostUsd(base.total_cost_usd ?? base.totalcostusd)
  if (!Number.isFinite(total_cost_usd) || total_cost_usd < 0) total_cost_usd = 0
  const stepSum = steps.reduce((acc, s) => acc + (Number.isFinite(s.costUsd) ? s.costUsd : 0), 0)
  if (total_cost_usd === 0 && stepSum > 0) total_cost_usd = stepSum

  const total_estimated_days =
    typeof base.total_estimated_days === "string"
      ? base.total_estimated_days
      : coerceEstimatedDays(base.total_estimated_days ?? base.totalEstimatedDays ?? base.totalestimateddays)

  const common_pitfalls = normalizeStringList(base.common_pitfalls ?? base.commonpitfalls)
  const success_tips = normalizeStringList(base.success_tips ?? base.successtips)

  return {
    steps,
    sources,
    routingNotes,
    cityRules: cityRulesTop,
    delivery,
    total_cost_usd,
    total_estimated_days,
    common_pitfalls,
    success_tips,
  }
}

/**
 * Counts resolvable vs missing references across the same structures shown in the admin enrichment preview
 * (official sources, step links, city-rule links, delivery rows).
 */
export function countEnrichmentReferenceQuality(env: VisaEnrichmentEnvelope): {
  verified: number
  notFound: number
} {
  let verified = 0
  let notFound = 0
  const tallyLink = (l: VisaEnrichmentLink) => {
    if (l.notFound) notFound += 1
    else verified += 1
  }
  for (const s of env.sources) {
    if (s.notFound) notFound += 1
    else verified += 1
  }
  for (const step of env.steps) {
    for (const l of step.links) tallyLink(l)
    for (const cr of step.cityRules) {
      if (cr.links) for (const l of cr.links) tallyLink(l)
    }
  }
  for (const cr of env.cityRules) {
    if (cr.links) for (const l of cr.links) tallyLink(l)
  }
  for (const d of env.delivery) {
    if (d.notFound) notFound += 1
    else verified += 1
  }
  return { verified, notFound }
}

export function validateEnrichmentEnvelope(env: VisaEnrichmentEnvelope): boolean {
  if (!Array.isArray(env.steps) || env.steps.length === 0) return false
  for (const s of env.steps) {
    if (typeof s.order !== "number" || !Number.isFinite(s.order)) return false
    if (typeof s.title !== "string" || !s.title) return false
    if (typeof s.description !== "string") return false
    if (typeof s.where !== "string") return false
    if (typeof s.notes !== "string") return false
    if (!Array.isArray(s.documentsNeeded) || !s.documentsNeeded.every((d) => typeof d === "string")) return false
    if (!Array.isArray(s.commonMistakes) || !s.commonMistakes.every((m) => typeof m === "string")) return false
    if (!Array.isArray(s.dependencies) || !s.dependencies.every((d) => typeof d === "number" && Number.isFinite(d))) {
      return false
    }
    if (!Array.isArray(s.appliesTo) || !s.appliesTo.every((x) => typeof x === "string")) return false
    if (!Array.isArray(s.restrictions) || !s.restrictions.every((x) => typeof x === "string")) return false
    if (!Array.isArray(s.links)) return false
    for (const l of s.links) {
      if (typeof l.label !== "string" || !l.label) return false
      if (l.notFound === true) continue
      if (typeof l.url !== "string" || !isPlausibleHttpUrl(l.url)) return false
    }
    if (!Array.isArray(s.cityRules)) return false
    for (const cr of s.cityRules) {
      if (typeof cr.city !== "string" || !cr.city) return false
      if (typeof cr.guidance !== "string") return false
      if (cr.links) {
        for (const l of cr.links) {
          if (typeof l.label !== "string" || !l.label) return false
          if (l.notFound === true) continue
          if (typeof l.url !== "string" || !isPlausibleHttpUrl(l.url)) return false
        }
      }
    }
    if (typeof s.estimatedDays !== "string") return false
    if (typeof s.costUsd !== "number" || !Number.isFinite(s.costUsd)) return false
  }
  if (!Array.isArray(env.sources)) return false
  for (const src of env.sources) {
    if (typeof src.kind !== "string" || !src.kind) return false
    if (typeof src.title !== "string" || !src.title) return false
    if (typeof src.notFound !== "boolean") return false
    if (!src.notFound) {
      if (typeof src.url !== "string" || !isPlausibleHttpUrl(src.url)) return false
    }
  }
  if (!Array.isArray(env.routingNotes) || !env.routingNotes.every((p) => typeof p === "string")) return false
  if (!Array.isArray(env.cityRules)) return false
  for (const cr of env.cityRules) {
    if (typeof cr.city !== "string" || !cr.city) return false
    if (typeof cr.guidance !== "string") return false
    if (cr.links) {
      for (const l of cr.links) {
        if (typeof l.label !== "string" || !l.label) return false
        if (l.notFound === true) continue
        if (typeof l.url !== "string" || !isPlausibleHttpUrl(l.url)) return false
      }
    }
  }
  if (!Array.isArray(env.delivery)) return false
  for (const d of env.delivery) {
    if (typeof d.title !== "string" || !d.title) return false
    if (typeof d.description !== "string") return false
    if (d.notFound === true) continue
    if (typeof d.url !== "string" || !isPlausibleHttpUrl(d.url)) return false
  }
  if (typeof env.total_cost_usd !== "number" || !Number.isFinite(env.total_cost_usd)) return false
  if (typeof env.total_estimated_days !== "string") return false
  if (!Array.isArray(env.common_pitfalls) || !env.common_pitfalls.every((p) => typeof p === "string")) return false
  if (!Array.isArray(env.success_tips) || !env.success_tips.every((p) => typeof p === "string")) return false
  return true
}

/** Parse + normalize + validate; returns null if unusable. */
export function parseModelJsonToVisaEnvelope(parsed: unknown): VisaEnrichmentEnvelope | null {
  const env = normalizeVisaEnrichmentPayload(parsed)
  if (!env) return null
  return validateEnrichmentEnvelope(env) ? env : null
}
